import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction'
import type { DayCellContentArg, DayHeaderContentArg, EventClickArg, EventContentArg, EventDropArg } from '@fullcalendar/core'
import { closestCorners, DndContext, DragOverlay, pointerWithin, useDroppable, type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core'
import { buzzOnPickUp, useDragSensors } from '@/hooks/useDragSensors'
import type { ShownEvent } from '@/hooks/useEvents'
import { usePeople } from '@/hooks/usePeople'
import { useSwipe } from '@/hooks/useSwipe'
import { useTaskActions } from '@/hooks/useTaskActions'
import { addDays, dateOf, endOfWeek, fmtDate, startOfWeek, weekdayName } from '@/logic/dates'
import { itemsOnDay, type PlanItem } from '@/logic/plan'
import type { Now } from '@/logic/status'
import { useCategories } from '@/store/categories'
import { setCalendarDate } from '@/store/ui'
import type { DateStr, Task } from '@/types'
import { usePrefs } from '@/store/prefs'
import { DayList } from './DayList'
import { DragGhost } from './DayView'
import { GridEntryContent } from './GridEntry'
import { calendarEntries, categoryColor, dropOnDay, nextPeriod, openEntry, plural, prevPeriod } from './shared'

const renderEntry = (arg: EventContentArg) => <GridEntryContent arg={arg} />
const NO_ITEMS: PlanItem[] = []

/** A pointer drops only on the header under it; the keyboard moves between headers. */
const collision: CollisionDetection = (args) => (args.pointerCoordinates ? pointerWithin(args) : closestCorners(args))

interface WeekGridProps {
  tasks: Task[]
  events: ShownEvent[]
  now: Now
  /** The selected day, which quick add uses; its week is shown. */
  date: DateStr
  wide: boolean
}

/**
 * Week: FullCalendar's time grid, all seven days side by side on a phone,
 * under an all-day row. A task sits in the hours only when it keeps a time on
 * its day. On a wide screen anything else, previews and what was done that day
 * sit in the all-day row. On a phone the columns are too narrow to read them,
 * so the all-day row keeps only all-day events, a dot under a day's number says
 * it has tasks, and the selected day's tasks are listed in full beneath the
 * grid: tapping anywhere in a day's column selects it, while an event or a
 * task in the hours still opens. A long press on a listed task lifts it, to
 * drop on a day's header. Dragging a task in the grid to another day plans it
 * there; events never move.
 */
export function WeekGrid({ tasks, events, now, date, wide }: WeekGridProps) {
  const today = now.date
  const ref = useRef<FullCalendar>(null)
  const navigate = useNavigate()
  const actions = useTaskActions()
  const people = usePeople()
  const byCategory = useCategories((s) => s.byId)

  const from = startOfWeek(date)
  const to = endOfWeek(date)
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const entries = useMemo(() => calendarEntries(tasks, events, from, to, today, categoryColor(byCategory), people, hideDone, !wide), [tasks, events, from, to, today, byCategory, people, hideDone, wide])
  // on a phone the all-day row only appears for a week with all-day events in it
  const allDayRow = wide || entries.some((e) => e.allDay)

  const byDay = useMemo(() => {
    const map = new Map<DateStr, PlanItem[]>()
    if (wide) return map
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(from, i)
      map.set(d, itemsOnDay(tasks, d, today, hideDone, true))
    }
    return map
  }, [wide, from, tasks, today, hideDone])

  // the arrows and swipes move the selected day; the grid follows when it leaves the week
  useEffect(() => {
    const api = ref.current?.getApi()
    if (api && startOfWeek(dateOf(api.getDate())) !== from) api.gotoDate(from)
  }, [from])

  // ---- dragging a listed task onto a day's header ----
  const sensors = useDragSensors()
  const [active, setActive] = useState<PlanItem | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const dragging = useRef(false)
  const finish = () => {
    setActive(null)
    setOver(null)
    // the touch that ends a drag also ends a swipe; let that one pass first
    window.setTimeout(() => {
      dragging.current = false
    }, 0)
  }
  const onDragStart = (e: DragStartEvent) => {
    dragging.current = true
    buzzOnPickUp(e)
    setActive((e.active.data.current?.item as PlanItem | undefined) ?? null)
  }
  const onDragOver = (e: DragOverEvent) => setOver(e.over ? String(e.over.id) : null)
  const onDragEnd = (e: DragEndEvent) => {
    const item = e.active.data.current?.item as PlanItem | undefined
    const target = e.over?.data.current?.day as DateStr | undefined
    finish()
    if (!item || !target || target === date) return
    actions.planOn(item.task, target)
  }

  const wrap = useSwipe(nextPeriod, prevPeriod, { blocked: () => dragging.current })

  const onDrop = (info: EventDropArg) => dropOnDay(info, (task, day) => actions.planOn(task, day))
  const onClick = (info: EventClickArg) => openEntry(info, (id, readOnly) => navigate(`/task/${id}`, { state: readOnly ? { readOnly: true } : undefined }))
  const onDateClick = wide ? undefined : (info: DateClickArg) => setCalendarDate(dateOf(info.date))

  // stable, so FullCalendar does not rebuild its cells on every parent render
  const dayHeader = useCallback(
    (arg: DayHeaderContentArg) => {
      const d = dateOf(arg.date)
      return <DayHead day={d} on={d === date} isToday={d === today} open={wide ? 0 : (byDay.get(d) ?? NO_ITEMS).filter((i) => !i.done).length} over={over === `head:${d}`} />
    },
    [date, today, wide, byDay, over],
  )
  const dayCellClass = useCallback((arg: DayCellContentArg) => (!wide && dateOf(arg.date) === date ? ['fcx-col--on'] : []), [wide, date])

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={finish}>
      <div className={`cweek ${wide ? 'cweek--wide' : ''}`}>
        <div className="fcx fcx--week" ref={wrap}>
          <FullCalendar
            ref={ref}
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            initialDate={from}
            firstDay={1}
            headerToolbar={false}
            allDaySlot={allDayRow}
            allDayText="All day"
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            scrollTime="07:00:00"
            scrollTimeReset={false}
            slotDuration="00:30:00"
            slotLabelInterval="01:00"
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            dayHeaderContent={dayHeader}
            dayCellClassNames={dayCellClass}
            height="100%"
            nowIndicator
            editable
            eventStartEditable
            eventDurationEditable={false}
            eventMinHeight={20}
            selectable={false}
            longPressDelay={350}
            eventLongPressDelay={350}
            dragRevertDuration={0}
            events={entries}
            eventContent={renderEntry}
            eventDrop={onDrop}
            eventClick={onClick}
            dateClick={onDateClick}
            eventOrder="start,-duration,rank,title"
          />
        </div>
        {!wide && (
          <div className="cweek__day">
            <DayList date={date} now={now} items={byDay.get(date) ?? NO_ITEMS} people={people} draggable />
          </div>
        )}
      </div>
      <DragOverlay dropAnimation={null}>{active ? <DragGhost item={active} /> : null}</DragOverlay>
    </DndContext>
  )
}

/** A day's header: the weekday over the number, a dot when it has tasks not yet done, as Month marks them; it selects the day, and takes a dropped task. */
function DayHead({ day, on, isToday, open, over }: { day: DateStr; on: boolean; isToday: boolean; open: number; over: boolean }) {
  const { setNodeRef } = useDroppable({ id: `head:${day}`, data: { day } })
  const label = [fmtDate(day), isToday ? 'today' : null, open ? plural(open, 'task') : null].filter(Boolean).join(', ')
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`fcx-dayhead ${on ? 'fcx-dayhead--on' : ''} ${isToday ? 'fcx-dayhead--today' : ''} ${over ? 'fcx-dayhead--over' : ''}`}
      aria-pressed={on}
      aria-label={label}
      onClick={() => setCalendarDate(day)}
    >
      <span className="fcx-dayhead__wd">{weekdayName(day)}</span>
      <span className="fcx-dayhead__n">{Number(day.slice(8))}</span>
      {open > 0 && <span className="fcx-dayhead__dot" aria-hidden="true" />}
    </button>
  )
}
